import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import type { CustomerOrder, CustomerOrderInvoice } from '../customerOrdersApi'
import type { CustomerProfile } from '../types'
import { useShopI18n } from '../../i18n'
import { buildCustomerAreaNavItems, CustomerAreaLayoutShell, customerMotionTransition } from './customer-area'
import {
  CustomerOrderDetailContent,
  CustomerOrderDetailErrorState,
  OrderDetailSkeleton,
  buildCustomerOrderDetailLabels,
  useOrderDetailSkeletonVisibility,
} from './customer-order-detail'
import { useCustomerInvoiceOpener } from '../hooks/useCustomerInvoiceOpener'

type CustomerOrderDetailPageProps = {
  token: string | null
  profile: CustomerProfile
  orderDetailId: string
  orderDetail: CustomerOrder | null
  orderDetailLoading: boolean
  orderDetailError: string
  orderInvoices: CustomerOrderInvoice[]
  orderInvoicesLoading: boolean
  orderInvoicesError: string
  onNavigate: (targetPath: string) => void
}

export const CustomerOrderDetailPage = ({
  token,
  profile,
  orderDetailId,
  orderDetail,
  orderDetailLoading,
  orderDetailError,
  orderInvoices,
  orderInvoicesLoading,
  orderInvoicesError,
  onNavigate,
}: CustomerOrderDetailPageProps) => {
  const { language, locale, t, toLocalizedPath } = useShopI18n()
  const reducedMotion = useReducedMotion()
  const prefersReducedMotion = Boolean(reducedMotion)
  const detailSkeletonVisible = useOrderDetailSkeletonVisibility({
    loading: orderDetailLoading,
    minVisibleMs: 920,
  })
  const labels = buildCustomerOrderDetailLabels(language)
  const { invoiceOpeningId, invoiceOpenError, handleOpenInvoice } = useCustomerInvoiceOpener({
    token,
    t,
  })

  const navItems = buildCustomerAreaNavItems({
    t,
    toLocalizedPath,
    language,
  })
  const skeletonTransition = prefersReducedMotion ? { duration: 0 } : { duration: 0.16, ease: customerMotionTransition.ease }
  const contentTransition = prefersReducedMotion ? { duration: 0 } : { duration: 0.26, ease: customerMotionTransition.ease }

  return (
    <CustomerAreaLayoutShell
      eyebrow={t('common.customerArea')}
      title={t('customer.orderDetail.title')}
      subtitle={
        orderDetail
          ? t('customer.orderDetail.idLabel', { id: orderDetail.orderNumber })
          : t('customer.orderDetail.idLabel', { id: orderDetailId })
      }
      navItems={navItems}
      activeNavId="orders"
      onNavigate={onNavigate}
      actions={[
        {
          id: 'back-orders',
          label: t('customer.orderDetail.backToOrders'),
          onClick: () => onNavigate(toLocalizedPath('/dashboard/orders')),
          variant: 'secondary',
        },
      ]}
    >
      <AnimatePresence mode="wait" initial={false}>
        {detailSkeletonVisible ? (
          <motion.div
            key="order-detail-loading"
            initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
            animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
            transition={skeletonTransition}
          >
            <OrderDetailSkeleton />
          </motion.div>
        ) : null}

        {!detailSkeletonVisible && !orderDetailLoading && orderDetailError ? (
          <CustomerOrderDetailErrorState
            prefersReducedMotion={prefersReducedMotion}
            contentTransition={contentTransition}
            error={orderDetailError}
            onBackToOrders={() => onNavigate(toLocalizedPath('/dashboard/orders'))}
            t={t}
          />
        ) : null}

        {!detailSkeletonVisible && !orderDetailLoading && orderDetail ? (
          <CustomerOrderDetailContent
            prefersReducedMotion={prefersReducedMotion}
            contentTransition={contentTransition}
            orderDetail={orderDetail}
            profile={profile}
            orderInvoices={orderInvoices}
            orderInvoicesLoading={orderInvoicesLoading}
            orderInvoicesError={orderInvoicesError}
            invoiceOpenError={invoiceOpenError}
            invoiceOpeningId={invoiceOpeningId}
            locale={locale}
            language={language}
            labels={labels}
            toLocalizedPath={toLocalizedPath}
            onNavigate={onNavigate}
            onOpenInvoice={(invoice) => {
              void handleOpenInvoice(invoice)
            }}
            t={t}
          />
        ) : null}
      </AnimatePresence>
    </CustomerAreaLayoutShell>
  )
}
